import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Shield, Check, X, Plus, Edit2, RotateCcw, AlertTriangle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import {
  allergiesService, PatientAllergy, AllergyCategory, AllergyCriticality,
} from '@/services/allergiesService';
import { fetchAllergies, AllergyOption } from '@/services/allergyService';
import { parseApiError } from '@/lib/apiErrors';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';

interface ReconcileAllergiesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  patientId: string;
  encounterId?: string;
  onCompleted?: () => void;
}

/** What the practitioner wants to do with this allergy row. */
type RowDecision =
  | 'pending'
  | 'confirm'   // verified, real allergy
  | 'modify'    // change criticality / reaction
  | 'refute'    // wasn't actually an allergy ("entered-in-error")
  | 'resolved'; // had an allergy, no longer relevant

interface RowState {
  decision: RowDecision;
  draftCriticality?: AllergyCriticality;
  draftReaction?: string;
}

interface AddDraft {
  // Stable client-side ID so partial-save retries skip already-created rows
  // even if the user mutates the pending list between attempts.
  id: string;
  display: string;
  category: AllergyCategory;
  criticality: AllergyCriticality;
  reaction: string;
  note: string;
}

const emptyAddDraft = (): AddDraft => ({
  id: globalThis.crypto.randomUUID(),
  display: '',
  category: 'medication',
  criticality: 'low',
  reaction: '',
  note: '',
});

/**
 * Allergy reconciliation — proper add / modify / confirm / refute / resolve flow.
 *
 * Each active allergy has a verification decision; the practitioner can also
 * add new allergies disclosed during the visit, modify criticality / reaction
 * details, refute entries that turn out to be wrong, or mark resolved
 * (e.g. desensitization). NKDA is captured as a structured affirmation when
 * the chart is empty.
 *
 * "Done" applies all per-row decisions, creates new entries, then records a
 * reconciliation audit event for Joint Commission compliance.
 */
const ReconcileAllergiesDialog: React.FC<ReconcileAllergiesDialogProps> = ({
  open, onOpenChange, clinicId, patientId, encounterId, onCompleted,
}) => {
  const qc = useQueryClient();
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [adds, setAdds] = useState<AddDraft[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draft, setDraft] = useState<AddDraft>(emptyAddDraft());
  const [substanceSuggestions, setSubstanceSuggestions] = useState<AllergyOption[]>([]);
  const [showSubstanceMenu, setShowSubstanceMenu] = useState(false);
  const substanceDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [nkda, setNkda] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: allergies = [], isLoading } = useQuery({
    queryKey: cacheKeys.patientAllergies(patientId, { activeOnly: true }),
    queryFn: () => allergiesService.getPatientAllergies(clinicId, patientId, { activeOnly: true }),
    enabled: open,
  });

  const latestReconciliationKey = useMemo(
    () => ['allergy-reconciliation-latest', clinicId, encounterId] as const,
    [clinicId, encounterId],
  );
  // Gate on `isFetching` (not `isFetched`). After save→invalidate→reopen,
  // `isFetched` is still true from the prior fetch — react-query keeps the
  // stale cached value while the background refetch is in flight. Waiting
  // for `isFetching` to flip false guarantees we hydrate with post-save data.
  const { data: latestReconciliation, isFetching: isLatestFetching } = useQuery({
    queryKey: latestReconciliationKey,
    queryFn: () => allergiesService.getLatestReconciliation(clinicId, encounterId!),
    enabled: open && !!encounterId,
  });

  // Refs survive across re-renders within a single open session. Used to
  // (a) hydrate notes/NKDA exactly once per open, so the user's typing isn't
  // clobbered by a late query resolve, and (b) make `finish()` idempotent on
  // retry — already-applied row updates, creates, and the recon-event POST
  // are skipped if a later step failed.
  const hasHydratedRef = useRef(false);
  const completedDecisionsRef = useRef<Set<string>>(new Set());
  const completedAddsRef = useRef<Set<string>>(new Set());
  const reconciliationRecordedRef = useRef(false);

  // Reset transient state when dialog opens fresh.
  useEffect(() => {
    if (open) {
      setRowStates({});
      setAdds([]);
      setShowAddForm(false);
      setDraft(emptyAddDraft());
      hasHydratedRef.current = false;
      completedDecisionsRef.current = new Set();
      completedAddsRef.current = new Set();
      reconciliationRecordedRef.current = false;
    }
  }, [open]);

  // Hydrate notes + NKDA exactly once per open, after the latest-event query
  // has settled. Skipping post-hydration changes prevents overwriting input
  // the user has already started typing.
  useEffect(() => {
    if (!open || hasHydratedRef.current) return;
    if (encounterId && isLatestFetching) return;
    hasHydratedRef.current = true;
    setNkda(latestReconciliation?.nkda ?? false);
    setNotes(latestReconciliation?.notes ?? '');
  }, [open, encounterId, isLatestFetching, latestReconciliation]);

  // Allergen typeahead via NLM Clinical Tables.
  useEffect(() => {
    if (substanceDebounce.current) clearTimeout(substanceDebounce.current);
    substanceDebounce.current = setTimeout(async () => {
      if (!draft.display.trim()) {
        setSubstanceSuggestions([]);
        return;
      }
      try {
        const results = await fetchAllergies(draft.display);
        setSubstanceSuggestions(results.slice(0, 8));
      } catch {
        // network failure → manual entry still works
      }
    }, 200);
    return () => {
      if (substanceDebounce.current) clearTimeout(substanceDebounce.current);
    };
  }, [draft.display]);

  const setDecision = (id: string, decision: RowDecision, init?: Partial<RowState>) => {
    setRowStates(s => ({
      ...s,
      [id]: {
        decision,
        draftCriticality: init?.draftCriticality ?? s[id]?.draftCriticality,
        draftReaction: init?.draftReaction ?? s[id]?.draftReaction,
      },
    }));
  };

  const addPendingAllergy = () => {
    if (!draft.display.trim()) {
      toast({ title: 'Add an allergen name', variant: 'destructive' });
      return;
    }
    setAdds(a => [...a, { ...draft }]);
    setDraft(emptyAddDraft());
    setShowAddForm(false);
    setNkda(false); // adding any allergy invalidates NKDA
  };

  const removePendingAdd = (id: string) => {
    setAdds(a => a.filter(x => x.id !== id));
  };

  const counts = useMemo(() => {
    const states = Object.values(rowStates);
    return {
      reviewed: allergies.length,
      added: adds.length,
      confirmed: states.filter(s => s.decision === 'confirm').length,
      modified: states.filter(s => s.decision === 'modify').length,
      refuted: states.filter(s => s.decision === 'refute').length,
      resolved: states.filter(s => s.decision === 'resolved').length,
    };
  }, [rowStates, adds, allergies.length]);

  const finish = async () => {
    setSaving(true);
    try {
      // Each successful step is recorded in a ref so a retry skips the
      // already-applied work instead of re-issuing (e.g.) an update on an
      // allergy that's already been confirmed server-side.
      for (const a of allergies) {
        const s = rowStates[a.id];
        if (!s || s.decision === 'pending') continue;
        if (completedDecisionsRef.current.has(a.id)) continue;
        if (s.decision === 'confirm') {
          await allergiesService.updateAllergy(clinicId, a.id, {
            verificationStatus: 'confirmed',
          });
        } else if (s.decision === 'modify') {
          await allergiesService.updateAllergy(clinicId, a.id, {
            verificationStatus: 'confirmed',
            criticality: s.draftCriticality ?? a.criticality ?? undefined,
            reactions: s.draftReaction?.trim()
              ? [{ description: s.draftReaction.trim() }]
              : undefined,
          });
        } else if (s.decision === 'refute') {
          await allergiesService.updateAllergy(clinicId, a.id, {
            verificationStatus: 'refuted',
            clinicalStatus: 'inactive',
          });
        } else if (s.decision === 'resolved') {
          await allergiesService.resolveAllergy(clinicId, a.id);
        }
        completedDecisionsRef.current.add(a.id);
      }

      // Create new allergies. The FHIRAllergyIntolerance row requires a
      // non-null `code_code` even for free-text entries, so derive one from
      // the display string when no SNOMED code is available — same pattern
      // PatientAllergyListTab uses.
      for (const a of adds) {
        if (completedAddsRef.current.has(a.id)) continue;
        const display = a.display.trim();
        await allergiesService.createAllergy(clinicId, patientId, {
          allergenCode: display.slice(0, 20),
          allergenDisplay: display,
          codeSystem: 'free-text',
          category: a.category,
          criticality: a.criticality,
          type: 'allergy',
          clinicalStatus: 'active',
          verificationStatus: 'confirmed',
          reactions: a.reaction.trim() ? [{ description: a.reaction.trim() }] : undefined,
          note: a.note.trim() || undefined,
          encounterId,
        });
        completedAddsRef.current.add(a.id);
      }

      // Record the reconciliation audit event. Guarded so a retry after a
      // post-event failure doesn't duplicate the event row.
      if (encounterId && !reconciliationRecordedRef.current) {
        await allergiesService.recordReconciliation(clinicId, encounterId, {
          reviewedCount: counts.reviewed,
          addedCount: counts.added,
          confirmedCount: counts.confirmed + counts.modified, // modify implies confirm
          refutedCount: counts.refuted,
          resolvedCount: counts.resolved,
          nkda: nkda && allergies.length === 0 && adds.length === 0,
          notes: notes.trim() || undefined,
        });
        reconciliationRecordedRef.current = true;
      }

      qc.invalidateQueries({ queryKey: cacheRoots.patientAllergies(patientId) });
      qc.invalidateQueries({ queryKey: latestReconciliationKey });
      toast({ title: 'Allergies reconciled', variant: 'success' });
      onCompleted?.();
      onOpenChange(false);
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Reconciliation failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = counts.added > 0 || counts.confirmed > 0 || counts.modified > 0
    || counts.refuted > 0 || counts.resolved > 0 || nkda || notes.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Shield className='h-4 w-4' /> Reconcile allergies
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {/* Summary chips */}
          <div className='flex flex-wrap gap-1.5 text-xs'>
            <Badge variant='outline'>Reviewed {counts.reviewed}</Badge>
            {counts.added > 0 && (
              <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>+{counts.added} new</Badge>
            )}
            {counts.confirmed + counts.modified > 0 && (
              <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>
                {counts.confirmed + counts.modified} confirmed
              </Badge>
            )}
            {counts.refuted > 0 && (
              <Badge className='bg-rose-100 text-rose-700 hover:bg-rose-100'>{counts.refuted} refuted</Badge>
            )}
            {counts.resolved > 0 && (
              <Badge className='bg-slate-100 text-slate-600 hover:bg-slate-100'>{counts.resolved} resolved</Badge>
            )}
          </div>

          {/* Active list */}
          {isLoading ? (
            <div className='flex justify-center py-4'>
              <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
            </div>
          ) : allergies.length === 0 ? (
            <div className='rounded border border-dashed border-slate-200 p-3 text-sm text-slate-500'>
              No active allergies on file. Add any the patient discloses, or check NKDA below to record
              "no known drug allergies" as of this visit.
            </div>
          ) : (
            <ul className='divide-y divide-slate-100 rounded border border-slate-200'>
              {allergies.map(a => (
                <AllergyRow
                  key={a.id}
                  allergy={a}
                  state={rowStates[a.id]}
                  onSetDecision={setDecision}
                />
              ))}
            </ul>
          )}

          {/* Pending adds */}
          {adds.length > 0 && (
            <div className='space-y-1'>
              <div className='text-xs font-semibold uppercase tracking-wide text-emerald-700'>To be added</div>
              <ul className='divide-y divide-slate-100 rounded border border-emerald-200 bg-emerald-50/30'>
                {adds.map(a => (
                  <li key={a.id} className='flex items-center justify-between gap-2 px-3 py-2 text-sm'>
                    <div className='min-w-0 flex-1'>
                      <div className='font-medium'>{a.display}</div>
                      <div className='text-xs text-slate-500'>
                        {a.category} · {a.criticality}{a.reaction ? ` · ${a.reaction}` : ''}
                      </div>
                    </div>
                    <Button size='sm' variant='ghost' onClick={() => removePendingAdd(a.id)}>
                      <X className='h-3 w-3' />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Add form */}
          {showAddForm ? (
            <div className='rounded border border-dashed border-slate-300 p-3 space-y-2'>
              <div className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Add allergy</div>
              <div className='grid gap-2 sm:grid-cols-2'>
                <div className='sm:col-span-2 relative'>
                  <Label htmlFor='add-substance' className='text-xs'>Allergen</Label>
                  <Input
                    id='add-substance'
                    autoFocus
                    autoComplete='off'
                    placeholder='e.g. Penicillin'
                    value={draft.display}
                    onChange={e => { setDraft(d => ({ ...d, display: e.target.value })); setShowSubstanceMenu(true); }}
                    onFocus={() => setShowSubstanceMenu(true)}
                    onBlur={() => setTimeout(() => setShowSubstanceMenu(false), 150)}
                  />
                  {showSubstanceMenu && substanceSuggestions.length > 0 && (
                    <ul className='absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md'>
                      {substanceSuggestions.map(s => (
                        <li key={s.value}>
                          <button
                            type='button'
                            className='w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50'
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDraft(d => ({ ...d, display: s.label }));
                              setShowSubstanceMenu(false);
                            }}
                          >
                            {s.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <Label className='text-xs'>Category</Label>
                  <Select value={draft.category} onValueChange={(v) => setDraft(d => ({ ...d, category: v as AllergyCategory }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='medication'>Medication</SelectItem>
                      <SelectItem value='food'>Food</SelectItem>
                      <SelectItem value='environment'>Environment</SelectItem>
                      <SelectItem value='biologic'>Biologic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className='text-xs'>Severity</Label>
                  <Select value={draft.criticality} onValueChange={(v) => setDraft(d => ({ ...d, criticality: v as AllergyCriticality }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='low'>Low</SelectItem>
                      <SelectItem value='high'>High</SelectItem>
                      <SelectItem value='unable-to-assess'>Unable to assess</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='add-reaction' className='text-xs'>Reaction</Label>
                  <Input
                    id='add-reaction'
                    placeholder='Hives, anaphylaxis, GI upset…'
                    value={draft.reaction}
                    onChange={e => setDraft(d => ({ ...d, reaction: e.target.value }))}
                  />
                </div>
                <div className='sm:col-span-2'>
                  <Label htmlFor='add-note' className='text-xs'>Note (optional)</Label>
                  <Input
                    id='add-note'
                    value={draft.note}
                    onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
                  />
                </div>
              </div>
              <div className='flex gap-2'>
                <Button size='sm' onClick={addPendingAllergy}>
                  <Plus className='mr-1 h-3 w-3' /> Add to list
                </Button>
                <Button size='sm' variant='ghost' onClick={() => { setShowAddForm(false); setDraft(emptyAddDraft()); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant='outline' size='sm' onClick={() => setShowAddForm(true)}>
              <Plus className='mr-1 h-3 w-3' /> Add allergy
            </Button>
          )}

          {/* NKDA toggle (only meaningful when list is empty + no pending adds) */}
          {allergies.length === 0 && adds.length === 0 && (
            <label className='flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-2 text-sm'>
              <Checkbox
                checked={nkda}
                onCheckedChange={(v) => setNkda(v === true)}
              />
              <span>
                <span className='font-medium'>Confirm NKDA</span>
                <span className='ml-1 text-xs text-slate-500'>— No known drug allergies as of today.</span>
              </span>
            </label>
          )}

          {/* Notes */}
          {encounterId && (
            <div className='space-y-1'>
              <Label htmlFor='rec-notes' className='text-xs'>Reconciliation notes (optional)</Label>
              <Textarea
                id='rec-notes'
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder='Verified with patient. Childhood penicillin reaction was nausea, not allergy — refuted.'
                className='min-h-[60px]'
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={finish} disabled={saving}>
            {saving
              ? <><Loader2 className='mr-2 h-4 w-4 animate-spin' /> Saving…</>
              : !hasChanges && allergies.length > 0
                ? <><Check className='mr-2 h-4 w-4' /> Confirm unchanged</>
                : <><Check className='mr-2 h-4 w-4' /> Done</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AllergyRow: React.FC<{
  allergy: PatientAllergy;
  state?: RowState;
  onSetDecision: (id: string, decision: RowDecision, init?: Partial<RowState>) => void;
}> = ({ allergy, state, onSetDecision }) => {
  const decision = state?.decision || 'pending';
  const struck = decision === 'refute' || decision === 'resolved';
  return (
    <li className='px-3 py-2 text-sm'>
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>
          <div className={`font-medium ${struck ? 'line-through text-slate-500' : ''}`}>
            {allergy.code_display}
          </div>
          <div className='mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500'>
            <span>{(allergy.category || []).join(', ') || 'unspecified'}</span>
            <span>· {allergy.criticality || 'unknown'}</span>
            <Badge variant='outline' className='text-[10px]'>
              {allergy.verification_status}
            </Badge>
          </div>
          {allergy.reactions && allergy.reactions.length > 0 && (
            <div className='mt-0.5 text-[11px] italic text-slate-500'>
              Reaction: {allergy.reactions[0].description
                || allergy.reactions[0].manifestations?.[0]?.display
                || '—'}
            </div>
          )}
        </div>
        <RowDecisionBadge decision={decision} />
      </div>

      {decision === 'modify' && (
        <div className='mt-2 grid gap-2 rounded-md bg-slate-50 p-2 sm:grid-cols-2'>
          <div>
            <Label className='text-[10px] uppercase'>Severity</Label>
            <Select
              value={state?.draftCriticality || allergy.criticality || 'low'}
              onValueChange={(v) => onSetDecision(allergy.id, 'modify', { draftCriticality: v as AllergyCriticality })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value='low'>Low</SelectItem>
                <SelectItem value='high'>High</SelectItem>
                <SelectItem value='unable-to-assess'>Unable to assess</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className='text-[10px] uppercase'>Reaction</Label>
            <Input
              defaultValue={allergy.reactions?.[0]?.description || ''}
              onChange={e => onSetDecision(allergy.id, 'modify', { draftReaction: e.target.value })}
              placeholder='Hives, anaphylaxis, GI upset…'
            />
          </div>
        </div>
      )}

      <div className='mt-2 flex flex-wrap gap-1.5'>
        <Button
          size='sm'
          variant={decision === 'confirm' ? 'default' : 'outline'}
          onClick={() => onSetDecision(allergy.id, 'confirm')}
          className='h-7 text-xs'
        >
          <Check className='mr-1 h-3 w-3' /> Confirm
        </Button>
        <Button
          size='sm'
          variant={decision === 'modify' ? 'default' : 'outline'}
          onClick={() => onSetDecision(allergy.id, 'modify', {
            draftCriticality: allergy.criticality || 'low',
            draftReaction: allergy.reactions?.[0]?.description || '',
          })}
          className='h-7 text-xs'
        >
          <Edit2 className='mr-1 h-3 w-3' /> Modify
        </Button>
        <Button
          size='sm'
          variant={decision === 'refute' ? 'default' : 'outline'}
          onClick={() => onSetDecision(allergy.id, 'refute')}
          className={`h-7 text-xs ${decision === 'refute' ? 'bg-rose-600 hover:bg-rose-700' : ''}`}
          title='Was never an allergy — entered in error'
        >
          <AlertTriangle className='mr-1 h-3 w-3' /> Refute
        </Button>
        <Button
          size='sm'
          variant={decision === 'resolved' ? 'default' : 'outline'}
          onClick={() => onSetDecision(allergy.id, 'resolved')}
          className='h-7 text-xs'
          title='Allergy no longer relevant (e.g. desensitization)'
        >
          <X className='mr-1 h-3 w-3' /> Resolved
        </Button>
        {decision !== 'pending' && (
          <Button
            size='sm'
            variant='ghost'
            onClick={() => onSetDecision(allergy.id, 'pending')}
            className='h-7 text-xs text-slate-500'
          >
            <RotateCcw className='mr-1 h-3 w-3' /> Reset
          </Button>
        )}
      </div>
    </li>
  );
};

const RowDecisionBadge: React.FC<{ decision: RowDecision }> = ({ decision }) => {
  if (decision === 'confirm') {
    return <Badge className='bg-emerald-100 text-emerald-700 hover:bg-emerald-100'>Confirm</Badge>;
  }
  if (decision === 'modify') {
    return <Badge className='bg-blue-100 text-blue-700 hover:bg-blue-100'>Modify</Badge>;
  }
  if (decision === 'refute') {
    return <Badge className='bg-rose-100 text-rose-700 hover:bg-rose-100'>Refute</Badge>;
  }
  if (decision === 'resolved') {
    return <Badge className='bg-slate-100 text-slate-600 hover:bg-slate-100'>Resolved</Badge>;
  }
  return null;
};

export default ReconcileAllergiesDialog;
